import { Badge, Box, Flex, MultiSelect, Text, useMantineColorScheme } from "@mantine/core";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import classes from "../SearchModal.module.css";

import { useMarketDecades } from "@/services/market/get-decades";
import { useMarketMakes } from "@/services/market/get-makes";
import { useSearchMarketCars } from "@/services/market/search-market-cars";

import type { MarketCar } from "@/types/market";
import type { Dispatch, FC, SetStateAction } from "react";

interface Props {
  makeFilter: string[];
  setMakeFilter: Dispatch<SetStateAction<string[]>>;
  isDisabled: boolean;
  yearFilter: string[];
  setYearFilter: Dispatch<SetStateAction<string[]>>;
  debounced: string;
  setFilteredResults: Dispatch<SetStateAction<MarketCar[]>>;
  setVisibleResultsCount: Dispatch<SetStateAction<number>>;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const SearchVisibleFilter: FC<Props> = ({
  makeFilter,
  setMakeFilter,
  isDisabled,
  yearFilter,
  setYearFilter,
  debounced,
  setFilteredResults,
  setVisibleResultsCount,
  setSearchTerm,
}) => {
  const { t } = useTranslation();

  const { data: makes = [] } = useMarketMakes();

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const { data: decades } = useMarketDecades();
  const { reset } = useSearchMarketCars();

  const decadeOptions = useMemo(() => {
    if (!decades?.length) return [];

    return decades.map((decade) => {
      const match = decade.match(/(\d{4})s/);
      if (!match) return { value: decade, label: decade };
      const startYear = parseInt(match[1], 10);
      if (startYear === 9999) {
        return {
          value: decade,
          label: t("market.tabs.9999s"), // "Ongoing" cars
        };
      }
      const endYear = startYear + 9;
      return {
        value: decade,
        label: `${startYear} - ${endYear}`,
      };
    });
  }, [decades, t]);

  return (
    <Flex mt="lg" w="100%" direction={{ base: "column", sm: "row" }} justify="space-between">
      <Box w="3.75em">
        <Text size="sm">{t("components.header.filter.filter_by")}</Text>
      </Box>
      <Flex justify="end" gap="sm">
        <MultiSelect
          data={makes.map((make) => ({
            value: make.id,
            label: make.name,
          }))}
          value={makeFilter}
          onChange={setMakeFilter}
          clearable
          searchable
          disabled={isDisabled}
          c={isDark ? "white" : "black"}
          w="150px"
          leftSection={
            makeFilter.length > 0 ? (
              <Badge variant="light" fw={500} miw={24} px={6}>
                {makeFilter.length}
              </Badge>
            ) : null
          }
          placeholder={`${t("components.header.filter.make")}`}
          aria-label={`${t("components.header.filter.make")} - ${makeFilter.length} ${t("selected")}`}
          className={classes["multiselect-search"]}
          styles={{
            pill: { display: "none" },
            label: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />
        <MultiSelect
          placeholder={t("components.header.filter.years")}
          data={decadeOptions}
          value={yearFilter}
          onChange={setYearFilter}
          clearable
          searchable
          c={isDark ? "white" : "black"}
          w="170px"
          leftSection={
            yearFilter.length > 0 ? (
              <Badge variant="light" fw={500} miw={24} px={6}>
                {yearFilter.length}
              </Badge>
            ) : null
          }
          disabled={isDisabled}
          aria-label={`${t("components.header.filter.years")} - ${yearFilter.length} ${t("selected")}`}
          className={classes["multiselect-search"]}
          styles={{
            pill: { display: "none" },
            label: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />
        <Box
          component="span"
          role="button"
          tabIndex={0}
          onClick={() => {
            reset();
            setFilteredResults([]);
            setVisibleResultsCount(10);
            setSearchTerm("");
            setMakeFilter([]);
            setYearFilter([]);
          }}
          style={{
            position: "relative",
            top: 5,
            color: isDark ? "#ff8787" : "var(--mantine-color-danger-7)",
            cursor:
              debounced.length < 1 && makeFilter.length === 0 && yearFilter.length === 0
                ? "default"
                : "pointer",
            display: "inline-block",
          }}
          aria-label="Cancel"
        >
          {t("components.header.filter.cancel_filters")}
        </Box>
      </Flex>
    </Flex>
  );
};

export default SearchVisibleFilter;
